import { inject, Injectable } from '@angular/core';
import { AppService } from './app.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, ReplaySubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AccountOnboardingData, AccountOnboardingStep, AgreementType, LiveTransferInfo, TopupConfig, UserInterface, WhiteLabelConfig } from 'voxr-shared';

type UserType = UserInterface & { _impersonated?: boolean; } | undefined;

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private app = inject(AppService);
  private http = inject(HttpClient);
  private _user$ = new ReplaySubject<UserType>(1);
  user$ = this._user$.asObservable();
  _user: UserType;
  customToken?: string;

  constructor() {
    this.app.idToken$.subscribe(async () => await this.current());
    this.user$.subscribe(user => this._user = user);
  }

  async loginByCustomToken(originalToken: string) {
    const { token } = await firstValueFrom(this.http.post<{ token: string }>(`${environment.app.serviceUrl}/user/custom-token`, { originalToken }));
    await this.app.customTokenLogin(token);
  }

  async current() {
    const user = this.app.idToken
      ? await firstValueFrom(this.http.get<UserInterface & { _impersonated?: boolean }>(`${environment.app.serviceUrl}/user/current`))
      : undefined;

    if(user) {
      if (this.impersonated()) user._impersonated = true;
      else if(!user.isEmailVerified && this.app.getAuthUser()?.emailVerified ) 
        this.app.refreshIdToken();
    }
    this._user$.next(user);
    return user;
  }

  getMissingProfileInfo() {
    const missing: string[] = [];
    const user = this._user;
    if (!user) return missing;


    return missing;
  }

  async updateProfile(user: UserInterface) {
    const result = await firstValueFrom(this.http.post<UserInterface>(`${environment.app.serviceUrl}/user/update-profile`, { phone: user.phone, profile: user.profile }));
    this._user$.next(result);
  }
  async updateWhiteLabelConfig(whiteLabel: WhiteLabelConfig) {
    await firstValueFrom(this.http.post<UserInterface>(`${environment.app.serviceUrl}/user/update-white-label`, { whiteLabel }));
    await this.current();
  }
  async updateTopupConfig(mode: 'reseller' | 'billing', config: TopupConfig) {
    await firstValueFrom(this.http.post<UserInterface>(`${environment.app.serviceUrl}/user/update-topup`, { mode, config }));
    await this.current();
  }

  async setTransferAvailability(data: LiveTransferInfo) {
    const result = await firstValueFrom(this.http.post<UserInterface>(`${environment.app.serviceUrl}/user/availability`, data));

    if (result._id === this._user?._id) {
      this._user$.next(result);
    }
  }

  async saveOnboarding(onboarded: AccountOnboardingStep[], onboardingData?: AccountOnboardingData) {
    return firstValueFrom(this.http.post<UserInterface>(`${environment.app.serviceUrl}/user/onboarded`, { onboarded, onboardingData }));
  }

  saveAgreement(agreement: AgreementType, info?: any) {
    return firstValueFrom(this.http.post(`${environment.app.serviceUrl}/user/agreed`, { agreement, info }));
  }

  get isOperator() {
    return this._user?.roles?.includes('super-admin') || this._user?.roles?.includes('operator') || this._user?._impersonated;
  }
  impersonated() {
    return localStorage.getItem('impersonate');
  }
  async impersonate(userId: string, resellerDomain: boolean | string = false) {
    let domain = '';
    if (typeof resellerDomain === 'string') domain = resellerDomain;
    else if (resellerDomain == true) {
      const info = await this.app.getResellerByUserId(userId);
      domain = info?.domains?.[0];
    }
    window.open(`${domain || '/'}?impersonate=${userId}`, '_blank');
  }
  unImpersonate() {
    localStorage.removeItem('impersonate');
    if (window.opener) window.close();
    else location.reload();
  }

  async setSessionData(data: any) {
    const { key } = await firstValueFrom(this.http.post<{ key: string }>(`${environment.app.serviceUrl}/user/session-data`, data));
    return key;
  }
  async getSessionData(key: string) {
    const { data } = await firstValueFrom(this.http.get<{ data: any }>(`${environment.app.serviceUrl}/user/session-data/${key}`));
    return data;
  }
}
